import { Menu, Transition } from '@headlessui/react'
import { ChevronDownIcon } from '@heroicons/react/solid'
import { useAppContext } from 'context/AppContext'
import { useTenantContext } from 'context/TenantContext'
import { Language } from 'context/types'
import getConfig from 'next/config'
import { useRouter } from 'next/router'
import { Fragment, useEffect, useState } from 'react'
import { classNames } from 'utils/dom-helpers'

const { NEXT_PUBLIC_STATIC_DOMAIN } = getConfig().publicRuntimeConfig
export const DEFAULT_LANGUAGE_CODE = 'en'
export const DEFAULT_LANGUAGE_RECORD = {
    text: 'English',
    code: DEFAULT_LANGUAGE_CODE,
    image_url: `https://${NEXT_PUBLIC_STATIC_DOMAIN}/images/flags/us.svg`,
    id: '',
}
export const DEFAULT_LANGUAGE_OPTIONS = [
    DEFAULT_LANGUAGE_RECORD,
    {
        text: '中文',
        code: 'zh-CN',
        image_url: `https://${NEXT_PUBLIC_STATIC_DOMAIN}/images/flags/cn.svg`,
    },
]

export function isValidLocale(code: string, locales: Language[]) {
    return locales?.some(
        (lang: Language) =>
            lang.locale.toUpperCase() === code?.toUpperCase()
    )
}

export function LanguageSelector() {
    const router = useRouter()
    const { preferred_languages } = useTenantContext()
    const { languages, locale, onLanguageChange } = useAppContext()
    const [language_options, setLanguageOptions] =
        useState<Record<string, string>[]>()
    const [selected_language, setSelectedLanguage] = useState<
        Record<string, string> | undefined
    >()

    // console.log("NEXT_PUBLIC_STATIC_DOMAIN", NEXT_PUBLIC_STATIC_DOMAIN);
    
    useEffect(() => {
        if (
            selected_language == undefined &&
            language_options &&
            language_options.length > 0
        ) {
            if (locale && locale.length > 0) {
                setSelectedLanguage(
                    language_options.find(
                        (pref) =>
                            pref.code.toLocaleLowerCase() ==
                            locale?.toLowerCase()
                    )
                )
            } else {
                setSelectedLanguage(DEFAULT_LANGUAGE_RECORD)
            }
        }
    }, [locale, language_options])

    useEffect(() => {
        if (languages && languages.length > 0 && preferred_languages) {
            if (router.pathname.startsWith('/book')) {
                setLanguageOptions(
                    preferred_languages?.map((pref) => {
                        const lang = languages.find(
                            (lang: Language) => lang.locale == pref.code
                        )
                        return {
                            code: pref.code,
                            text: lang?.name,
                            image_url: lang?.imageUrl,
                        }
                    }) as any
                )
            } else {
                setLanguageOptions(DEFAULT_LANGUAGE_OPTIONS)
            }
        }
    }, [router.pathname, preferred_languages, languages])

    function handleOnClick(lang: Record<string, string>) {
        setSelectedLanguage(lang)
        onLanguageChange(lang.code)
    }

    return (
        <Menu
            as="div"
            className={classNames('relative inline-block text-left')}
        >
            {({ open }) => (
                <>
                    <div>
                        <Menu.Button
                            className={classNames(
                                'inline-flex justify-center items-center w-full',
                                '  sm:bg-transparent',
                                'text-sm font-medium text-gray-700',
                                'hover:bg-primary-lighter '
                            )}
                        >
                            {selected_language?.image_url ? (
                                <img
                                    src={selected_language.image_url}
                                    className={classNames(
                                        '  w-6 h-6 lang-img'
                                    )}
                                    alt="Selected Language Image"
                                />
                            ) : (
                                <label className="text-custom-dark-cyan-blue uppercase my-auto">
                                    {selected_language?.text}
                                </label>
                            )}
                            
                            <ChevronDownIcon
                                            className="w-5 h-5 ml-2 -mr-1 text-custom-dark-cyan-blue"
                                            aria-hidden="true"
                                        />
                            {/* <i
                                className="feather-chevron-down -mr-1 ml-2 h-5 w-5 text-primary-light hidden sm:block"
                                aria-hidden="true"
                            /> */}
                        </Menu.Button>
                    </div>

                    <Transition
                        as={Fragment}
                        enter="transition ease-out duration-100"
                        enterFrom="transform opacity-0 scale-95"
                        enterTo="transform opacity-100 scale-100"
                        leave="transition ease-in duration-75"
                        leaveFrom="transform opacity-100 scale-100"
                        leaveTo="transform opacity-0 scale-95"
                        show={open}
                    >
                        <Menu.Items
                            className={classNames(
                                'origin-top-right right-0',
                                'absolute mt-2 w-max px-3 py-1',
                                'rounded-md shadow-lg bg-gray-100 ring-1 ring-black ring-opacity-5',
                                'focus:outline-none max-h-[50vh] overflow-y-auto z-20'
                            )}
                        >
                                {language_options &&
                                    language_options.map(
                                        (lang: any, index: number) => {
                                            return (
                                                <Menu.Item key={index}>
                                                    {({ active }) => (
                                                        <button
                                                            className={classNames(
                                                                'block text-sm text-gray-700 rounded-md items-center hover:bg-primary-lighter'
                                                            )}
                                                            onClick={() => {
                                                                handleOnClick(
                                                                    lang
                                                                )
                                                            }}
                                                        >
                                                            <label
                                                                htmlFor={`locale-${index}`}
                                                                className="flex min-w-0 flex-1 text-gray-500 h-full px-1.5 py-2"
                                                            >
                                                                {lang?.image_url ? (
                                                                    <img
                                                                        src={
                                                                            lang.image_url
                                                                        }
                                                                        className={classNames(
                                                                            ' rounded-full w-7 h-7'
                                                                        )}
                                                                        alt={
                                                                            lang.text
                                                                        }
                                                                    />
                                                                ) : (
                                                                    <i className="feather-globe w-8 h-8 text-[32px]" />
                                                                )}

                                                                <label className="text-custom-dark-cyan-blue font-bold uppercase ml-2 my-auto text-xs">
                                                                    {
                                                                        lang.text
                                                                    }
                                                                </label>
                                                            </label>
                                                        </button>
                                                    )}
                                                </Menu.Item>
                                            )
                                        }
                                    )}                         
                        </Menu.Items>
                    </Transition>
                </>
            )}
        </Menu>
    )
}

export default LanguageSelector
